import { useState, useEffect } from "react";
import {
  ArrowClockwise,
  ContactlessPayment,
  CreditCard,
  Eye,
  EyeSlash,
  MinusCircle,
  // Money,
  Plus,
  Spinner,
} from "@phosphor-icons/react";
import visa from "../assets/images/visa.svg";
import mastercard from "../assets/images/visaro-master-card.png";
import useCard from "../hooks/useCard";
import useInitializeAddCard, {
  getAddCardAuthorizationUrl,
} from "../hooks/useInitializeAddCard";
import useDeleteCard from "../hooks/useDeleteCard";
import { toast } from "sonner";
import DefaultModal from "../components/DefaultModal";
import { AnimatePresence, motion } from "framer-motion";
import cardBg from "../assets/images/cardBgDesign.svg";
import moment from "moment";
import { 
  // Link, 
  useLocation 
} from "react-router-dom";
import { formatAsMoney, maskBalance } from "../helpers/numbers";
import { useAuth } from "../context/AuthContext";
import useMoment from "../hooks/useMoment";
import useAlertEnquiry from "../hooks/useAlertEnquiry";

const Cards = () => {
  const { pathname } = useLocation();
  const { card, isLoading } = useCard();
  const [showDefaultModal, setShowDefaultModal] = useState(false);

  const { isRefetching, refetch } = useAuth();
  const { 
    alertEnquiryData, 
    refetchAlertAccount, 
    isRefetchingAlertAccount,
    fetchingAlertAccount 
  } = useAlertEnquiry();

  const [isVisible, setIsVisible] = useState(true);

  const [lastRefreshed, setLastRefrehsed] = useState<Date | null>(null);

  const lastRefreshedMoment = useMoment(lastRefreshed);

  const [activeTab, setActiveTab] = useState<string | null>("linked");

  useEffect(() => {
    if (pathname.includes("new-virtual-card")) {
      setActiveTab("virtual");
    }
  }, [pathname]);

  const { deleteCard } = useDeleteCard(card?.data?.id);
  const { initializeAddCard, initializing, initializeAddCardError } =
    useInitializeAddCard({
      onSuccess(data) {
        const authorizationUrl = getAddCardAuthorizationUrl(data);

        if (authorizationUrl) {
          toast.success("Redirecting to secure card authorization...");
          window.location.assign(authorizationUrl);
          return;
        }

        toast.error("We couldn't start card linking. Please try again.");
      },
      onError(error) {
        toast.error(error.message);
      },
    });

  const handleDeleteCard = () => {
    deleteCard(
      {},
      {
        onSuccess() {
          toast.success("Card deleted successfully");
        },
        onError(e) {
          toast.error(e.message);
        },
      }
    );
  };
  return (
    <div className="p-5 md:p-10 bg-gray-50">
      <div className="max-w-6xl mx-auto grid  lg:grid-cols-3 gap-5">
        <div className="flex flex-col gap-5 lg:col-span-2">
          <div className="md:bg-white drop-shadow-sm md:p-5 rounded-xl flex flex-col gap-5 items-center justify-center">
            <div className="flex items-start justify-between w-full">
              <div className="flex flex-col">
                <div className="text-xl font-medium">Your Cards</div>
                <div className="text-gray-500">
                  Link your card to fund your wallet, make payments, and access
                  virtual cards.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab("virtual")}
                className="flex items-center gap-2 font-medium text-sm p-2 bg-gray-50 rounded-md border hover:border-visaro-primary-700 border-transparent transition-all"
              >
                <Plus />
                <span>New Virtual Card</span>
              </button>
            </div>
            <div className="bg-gray-100 p-1.5 rounded-md max-w-96 w-full text-sm flex items-center gap-2">
              <AnimatePresence>
                <button
                  onClick={() => {
                    setActiveTab("linked");
                  }}
                  type="button"
                  className="rounded flex items-center text-center px-6 py-2 gap-2 cursor-pointer flex-1 justify-center relative"
                >
                  {activeTab === "linked" && (
                    <motion.div
                      layoutId="pill"
                      className="bg-white rounded absolute top-0 left-0 w-full  h-full shadow-sm"
                    ></motion.div>
                  )}
                  <div className="flex items-center justify-center gap-2 relative z-1">
                    <div>Linked Card</div>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("virtual")}
                  type="button"
                  className="rounded flex items-center justify-center text-center px-2 py-2 gap-2 flex-1 relative cursor-pointer"
                >
                  {activeTab === "virtual" && (
                    <motion.div
                      layoutId="pill"
                      className="bg-white rounded absolute top-0 left-0 w-full  h-full shadow-sm"
                    ></motion.div>
                  )}
                  <div className="relative z-1">Virtual Cards</div>
                </button>
              </AnimatePresence>
            </div>
            {activeTab === "linked" && (
              <div className="flex flex-col gap-5 w-full">
                {isLoading ? (
                  <div className="text-gray-500">Loading your cards...</div>
                ) : card?.data ? (
                  <div className="flex flex-col gap-5 justify-center items-center py-2">
                    <div className="bg-gradient-to-r from-visaro-orange to-orange-600 p-5 rounded-xl max-w-80 w-full aspect-[1/0.6] flex flex-col justify-between text-white relative overflow-hidden">
                      <img
                        src={cardBg}
                        alt="carb_bg"
                        className="absolute z-0 left-0 scale-105 w-full h-full object-contain object-center top-0"
                      />
                      <div className="flex justify-between items-center">
                        <img
                          src={
                            card?.data?.card_type === "visa" ? visa : mastercard
                          }
                          alt="logo"
                          className="w-12"
                        />
                        <ContactlessPayment className="size-10 opacity-80" />
                      </div>
                      <div>
                        <div className="text-2xl tracking-wider font-grotesk">
                          **** {card?.data?.last_four_digit}
                        </div>
                        <div className="flex flex-col">
                          <div className="uppercase text-xs opacity-80 font-medium">
                            Expiry
                          </div>
                          <div className="font-grotesk">
                            {card?.data?.exp_month}/
                            {card?.data?.exp_year?.slice(-2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => initializeAddCard({})}
                        disabled={initializing}
                        className="p-1.5 flex gap-2 rounded-md border items-center text-sm justify-center px-5 w-fit font-medium hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <span className="bg-green-50 text-green-500 p-1 rounded-full">
                          {initializing ? (
                            <Spinner className="animate-spin" size={18} />
                          ) : (
                            <CreditCard size={18} />
                          )}
                        </span>
                        <span>{initializing ? "Redirecting..." : "Update Card"}</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowDefaultModal(true);
                        }}
                        className="p-1.5 flex gap-2 rounded-md border items-center text-sm justify-center px-5 w-fit font-medium hover:bg-gray-100"
                      >
                        <span className="bg-red-50 text-red-500 p-1 rounded-full">
                          <MinusCircle size={18} />
                        </span>
                        <span>Remove Card</span>
                      </button>
                    </div>
                    <div className="flex flex-col items-stretch w-full gap-4">
                      <div className="font-medium">Card Details</div>
                      <div className="flex justify-between items-center w-full">
                        <div>Card Type</div>
                        <div className="capitalize font-medium">
                          {card?.data?.card_type}
                        </div>
                      </div>
                      <div className="flex justify-between items-center w-full">
                        <div>Exp Date</div>
                        <div className="capitalize font-medium">
                          {card?.data?.exp_month}/{card?.data?.exp_year}
                        </div>
                      </div>
                      <div className="flex justify-between items-center w-full">
                        <div>Last Charged</div>
                        <div className="capitalize font-medium">
                          {moment(card?.data?.last_charged).format(
                            "DD MMM, YYYY - hh:mm A"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full flex flex-col gap-5">
                    <button
                      className="text-green-600 font-medium hover:bg-gray-50 transition border rounded-md border-dashed border-deepBlue p-2 flex items-center flex-col gap-2 py-10 w-full"
                      onClick={() => initializeAddCard({})}
                      disabled={initializing}
                    >
                      <div className="bg-green-50 rounded-full p-2">
                        <CreditCard size={30} />
                      </div>
                      <span>{initializing ? "Linking..." : "Link a Card"}</span>
                      {initializeAddCardError && (
                        <div className="text-red-600 mt-2">
                          {initializeAddCardError.message}
                        </div>
                      )}
                    </button>
                    <div className="text-gray-600 text-center text-sm">
                      Link your card to fund your wallet instantly and enjoy
                      seamless payments. Linking a card also helps us get to
                      know you better, increasing your chances of qualifying for
                      higher credit limits in the future.
                    </div>
                  </div>
                )}
              </div>
            )}
            {activeTab === "virtual" && (
              <div className="flex flex-col gap-5 w-full items-center justify-center">
                {card?.data ? (
                  <div className="flex justify-center w-full">
                    <div className="bg-gradient-to-r from-visaro-orange to-orange-600 p-5 rounded-xl max-w-80 w-full aspect-[1/0.6] flex flex-col justify-between text-white relative overflow-hidden">
                      <img
                        src={cardBg}
                        alt="card_bg"
                        className="absolute z-0 left-0 scale-105 w-full h-full object-contain object-center top-0"
                      />
                      <div className="flex justify-between items-center relative z-10">
                        <img
                          src={card?.data?.card_type === "visa" ? visa : mastercard}
                          alt="logo"
                          className="w-12"
                        />
                        <ContactlessPayment className="size-10 opacity-80" />
                      </div>
                      <div className="relative z-10">
                        <div className="text-2xl tracking-wider font-grotesk">
                          **** {card?.data?.last_four_digit}
                        </div>
                        <div className="flex flex-col">
                          <div className="uppercase text-xs opacity-80 font-medium">
                            Expiry
                          </div>
                          <div className="font-grotesk">
                            {card?.data?.exp_month}/{card?.data?.exp_year?.slice(-2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full flex flex-col gap-2 items-center justify-center py-8 text-gray-500 text-center">
                    <CreditCard size={48} className="opacity-50" />
                    <p className="font-medium">No virtual cards yet</p>
                    <p className="text-sm">Link a card in the Linked Card tab to see it here.</p>
                  </div>
                )}
                <div className="flex flex-col items-center justify-center">
                  <div className="text-sm text-gray-600 font-medium">
                    Available Balance
                  </div>
                  <div className="flex flex-col items-center justify-center gap-1">
                    <div className="w-full tracking-wide text-2xl font-medium">
                      ₦{" "}
                      {isVisible
                        ? formatAsMoney(alertEnquiryData?.Message?.AvailableBalance || 0.0)
                        : maskBalance(alertEnquiryData?.Message?.AvailableBalance || 0.0)}
                    </div>
                    <div className="text-xs font-medium text-gray-400">
                      {fetchingAlertAccount ? "Loading..." : alertEnquiryData?.Message?.AccountNumber || "Account Not Found"}
                    </div>
                    <button
                      onClick={() => {
                        setIsVisible((prev) => !prev);
                      }}
                      className="bg-gray-100 rounded-full p-2 text-visaro-orange mt-2"
                    >
                      {isVisible ? (
                        <Eye size={14} weight="bold" />
                      ) : (
                        <EyeSlash size={14} weight="bold" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-row gap-2">
                  <button
                    type="button"
                    className="p-1.5 flex gap-2 rounded-md border items-center text-sm justify-center px-5 w-fit font-medium hover:bg-gray-100"
                  >
                    <span className="bg-green-50 text-green-500 p-1 rounded-full">
                      <CreditCard size={18} />
                    </span>
                    <span>Fund Card</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowDefaultModal(true);
                    }}
                    className="p-1.5 flex gap-2 rounded-md border items-center text-sm justify-center px-5 w-fit font-medium hover:bg-gray-100"
                  >
                    <span className="bg-red-50 text-red-500 p-1 rounded-full">
                      <MinusCircle size={18} />
                    </span>
                    <span>Delete Card</span>
                  </button>
                </div>
                <div className="flex-col flex gap-4 w-full bg-gray-50 p-3">
                  <div className="font-medium text-lg">Transaction History</div>
                  <div>
                    <div className="text-gray-500 text-center py-10">
                      No transactions yet. Your recent card activity will appear
                      here.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {card?.data && (
          <div className="md:p-2 rounded-xl md:bg-white drop-shadow-sm self-start">
            <div className="bg-gray-50 p-3 flex flex-col gap-2 rounded-lg flex-1 h-full">
              <div>Wallet Balance</div>
              <div className="w-full text-3xl font-medium flex items-center justify-between gap-2">
                <div className="flex flex-col gap-0.5 flex-1">
                  <div className="w-full tracking-wide">
                    ₦{" "}
                    {isVisible
                      ? formatAsMoney(alertEnquiryData?.Message?.AvailableBalance || 0.0)
                      : maskBalance(alertEnquiryData?.Message?.AvailableBalance || 0.0)}
                  </div>
                  <div className="text-xs font-medium text-gray-400 tracking-normal">
                    {fetchingAlertAccount ? "Loading..." : alertEnquiryData?.Message?.AccountNumber || "Account Not Found"}
                  </div>
                </div>
                <div className="w-fit justify-end flex items-center gap-2">
                  <button
                    onClick={() => {
                      Promise.all([refetch(), refetchAlertAccount()]).then(() => {
                        setLastRefrehsed(new Date());
                      });
                    }}
                    className="bg-gray-100 rounded-full p-2 text-visaro-orange"
                  >
                    {isRefetching || isRefetchingAlertAccount ? (
                      <Spinner
                        className="animate-spin"
                        size={14}
                        weight="bold"
                      />
                    ) : (
                      <ArrowClockwise size={14} weight="bold" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setIsVisible((prev) => !prev);
                    }}
                    className="bg-gray-100 rounded-full p-2 text-visaro-orange"
                  >
                    {isVisible ? (
                      <Eye size={14} weight="bold" />
                    ) : (
                      <EyeSlash size={14} weight="bold" />
                    )}
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {lastRefreshed && `Last updated ${lastRefreshedMoment}`}
              </div>
              <hr className="border-dashed my-4" />
              {/* <div className="flex flex-col gap-4 justify-center items-center px-5">
                <Link
                  to="/fund/fund-with-card"
                  className="p-1.5 flex gap-2 rounded-md border items-center text-sm justify-center px-5 w-fit font-medium hover:bg-gray-100"
                >
                  <span className="bg-green-50 text-green-500 p-1 rounded-full">
                    <Money size={18} />
                  </span>
                  <span>Fund Wallet</span>
                </Link>
                <p className="text-sm text-gray-500 text-center">
                  You can use your linked card to fund your wallet instantly and
                  make secure payments.
                </p>
              </div> */}
            </div>
          </div>
        )}
        {showDefaultModal && (
          <DefaultModal
            question={"Are you sure you want to remove this card?"}
            setShowModal={setShowDefaultModal}
            handleClick={handleDeleteCard}
          />
        )}
      </div>
    </div>
  );
};

export default Cards;
